import React from 'react';
import { useNavigate } from 'react-router-dom';
import { DesiredAction, DesiredActionKey } from '@dreamai/types';
import { getActionKeyLabel } from '../../../lib/desiredActionFields';
import type { CompletedAction } from './ProjectAppBlockRenderer';
import { ReactComponent as ActionBuyIcon } from '../../../icons/action-buy.svg';
import { ReactComponent as ActionResearchIcon } from '../../../icons/action-research.svg';
import { ReactComponent as ActionEmailSendIcon } from '../../../icons/action-email-send.svg';
import { ReactComponent as ActionBuyAndActIcon } from '../../../icons/action-buy-and-act.svg';
import { ReactComponent as ActionCalendarInviteIcon } from '../../../icons/action-calendar-invite.svg';
import { ReactComponent as ActionBringAndBuyIcon } from '../../../icons/action-bring-and-buy.svg';
import { ReactComponent as ActionDraftEmailIcon } from '../../../icons/action-draft-email.svg';
import { ReactComponent as ActionPhoneIcon } from '../../../icons/action-phone.svg';
import { ReactComponent as ActionPresentOptionsIcon } from '../../../icons/action-present-options.svg';
import { ReactComponent as ActionFillInIcon } from '../../../icons/action-fill-in.svg';
import { ReactComponent as ActionReserveIcon } from '../../../icons/action-reserve.svg';
import { ReactComponent as ActionActIcon } from '../../../icons/action-act.svg';
import { ReactComponent as ActionOtherIcon } from '../../../icons/action-other.svg';
import { ReactComponent as TransportItineraryIcon } from '../../../icons/transport-itinerary.svg';
import { ReactComponent as ActionProjectIcon } from '../../../icons/action-project.svg';
import { ReactComponent as MagicWandIcon } from '../../../icons/magic-wand.svg';
import { ReactComponent as AgentSearchIcon } from '../../../icons/agent-search.svg';
import { ReactComponent as ActionCreateIcon } from '../../../icons/action-create.svg';
import './research-suggestions.scss';

export const ACTION_ICONS: Record<
  DesiredActionKey,
  React.FC<React.SVGProps<SVGSVGElement>>
> = {
  buy: ActionBuyIcon,
  reserve: ActionReserveIcon,
  research: ActionResearchIcon,
  present_options: ActionPresentOptionsIcon,
  email_from_assistant: ActionEmailSendIcon,
  draft_email_from_user: ActionDraftEmailIcon,
  phone_from_assistant: ActionPhoneIcon,
  fill_in_by_assistant: ActionFillInIcon,
  send_calendar_invite: ActionCalendarInviteIcon,
  bring: TransportItineraryIcon,
  bring_and_buy: ActionBringAndBuyIcon,
  act: ActionActIcon,
  buy_and_act: ActionBuyAndActIcon,
  project: ActionProjectIcon,
  autopilot: MagicWandIcon,
  other: ActionOtherIcon,
  enhance: MagicWandIcon,
  find_more: AgentSearchIcon,
  create: ActionCreateIcon,
};

interface ResearchSuggestionsProps {
  desiredActions: DesiredAction[];
  onSuggestionClick?: (action: DesiredAction) => void;
  agentName?: string;
  completedActions?: CompletedAction[];
}

const ResearchSuggestions: React.FC<ResearchSuggestionsProps> = ({
  desiredActions,
  onSuggestionClick,
  agentName,
  completedActions = [],
}) => {
  const navigate = useNavigate();
  if (!desiredActions || desiredActions.length === 0) return null;

  return (
    <div className='research-suggestions'>
      <div className='research-suggestions-label'>What we can do next</div>
      <div className='research-suggestions-scroll'>
        {desiredActions.map((action, index) => {
          const Icon = ACTION_ICONS[action.key];
          const actionLabel = getActionKeyLabel(action.key, agentName);
          const completed = completedActions.find(
            (c) => c.id === `suggestion:${action.key}`,
          );
          if (completed) {
            return (
              <div
                key={`${action.key}-${index}`}
                className={`research-suggestion-card completed ${completed.phase}`}
              >
                <span className='research-suggestion-todo'>Todo Created</span>
                {completed.phase === 'view' && (
                  <button
                    className='research-suggestion-view-btn'
                    onClick={() => navigate('/')}
                  >
                    View in Todo List →
                  </button>
                )}
              </div>
            );
          }
          return (
            <button
              key={`${action.key}-${index}`}
              className={`research-suggestion-card${action.default ? ' default' : ''}`}
              onClick={() => onSuggestionClick?.(action)}
            >
              {Icon && <Icon className='research-suggestion-icon' />}
              <span className='research-suggestion-todo'>
                {action.todoText || actionLabel}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ResearchSuggestions;
